import toArray from "lodash/toArray";

import { useTranslator } from "@eo-locale/react";

import { ChangeEvent, useEffect } from "react";

import { CommonDropdownQuestion } from "@/storybook-components";

import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

import {
  removeSurveyAnswers,
  setSurveyAnswers
} from "@/redux/features/survey/slice";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";
import { spacing, textStyles } from "./question-styles";

interface IDropDownQuestion {
  question: Question;
}

const DropDownQuestion = ({ question }: IDropDownQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { isEmbed } = useEmbeds();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, options, saving } = survey;
  const { settings } = useAppSelector((state) => state.settings);
  const { googleTranslationEnabled } = settings;

  const error = errors[question.id] || {};
  const answer = answers[question.id] ?? {};

  // ToDo: Remove after BE implementation
  const sortedOptions = toArray(options)
    .filter((option) => question.id === option.questionId)
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
  const selectedOption = sortedOptions.find((o) => o.id === answer?.id);
  const answeredOption = answer?.id
    ? { ...selectedOption, label: selectedOption.name, value: answer?.id }
    : null;

  useEffect(() => {
    if (!answers[question.id]?.id) {
      dispatch(
        removeSurveyAnswers({
          questionId: question.id
        })
      );
      return;
    }
  }, [answers, dispatch, question.id]);

  const handleChange = (e: { value: string }): void => {
    // need to trigger Google Translate upon selecting a value
    googleTranslateInit(isEmbed, googleTranslationEnabled);
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { id: parseInt(e?.value) }
      })
    );
  };

  const handleOtherChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          id: answer.id,
          value: e.target.value
        }
      })
    );
  };

  return (
    <CommonDropdownQuestion
      layoutProps={{ width: "100%" }}
      isInvalid={!!error.code}
      isRequired={question.required}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answer={answeredOption}
      otherAnswer={answer?.value ?? ""}
      placeholder={translate("survey.selectOption")}
      answerAriaLabel={translate("survey.selectOption")}
      questionId={question?.id}
      isDisabled={!!saving}
      changeEventHandler={handleChange}
      isClearable={true}
      isSearchable={false}
      contentClassName="ehq-dropdownContainer"
      size="lg"
      options={sortedOptions.map((option) => ({
        ...option,
        value: option.id,
        label: option.name
      }))}
      handleOtherChange={handleOtherChange}
      optionClassName="ehq-surveyDropdownTextarea"
      answerProps={{ ...textStyles?.content }}
      onMenuClose={() => {
        googleTranslateInit(isEmbed, googleTranslationEnabled);
      }}
      onMenuOpen={() => {
        googleTranslateInit(isEmbed, googleTranslationEnabled);
      }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
    />
  );
};

export default DropDownQuestion;
